import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi } from '../../services/api';
import { beginNewAuthSession, clearAllAuthStorage, readInitialAuthTokenForStore } from '../../utils/authSession';

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

const initialAuthToken = readInitialAuthTokenForStore();
const initialUser = getStoredUser();

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials);
      if (response.token) {
        localStorage.setItem('token', response.token);
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        beginNewAuthSession();
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const verify2FALogin = createAsyncThunk(
  'auth/verify2FALogin',
  async ({ tempToken, code }, { rejectWithValue }) => {
    try {
      const response = await authApi.verify2FALogin({ tempToken, code });
      if (response.token) {
        localStorage.setItem('token', response.token);
        if (response.user) {
          localStorage.setItem('user', JSON.stringify(response.user));
        }
        beginNewAuthSession();
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const signup = createAsyncThunk(
  'auth/signup',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await authApi.signup(userData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authApi.getProfile();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authApi.updateProfile(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: initialUser,
    token: initialAuthToken,
    loading: false,
    error: null,
    isAuthenticated: !!initialAuthToken,
    twoFactorPending: false,
    twoFactorData: null,
  },
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.twoFactorPending = false;
      state.twoFactorData = null;
      try {
        localStorage.removeItem('user');
      } catch (_) {}
      clearAllAuthStorage();
    },
    cancel2FAPending: (state) => {
      state.twoFactorPending = false;
      state.twoFactorData = null;
      state.error = null;
    },
    /** Other tab (or device with shared storage) signed in; clear Redux only — shared token stays for the new session. */
    supersededByRemoteLogin: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.twoFactorPending = false;
      state.twoFactorData = null;
      try {
        localStorage.removeItem('user');
      } catch (_) {}
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.require2FA) {
          state.twoFactorPending = true;
          state.twoFactorData = {
            tempToken: action.payload.tempToken,
            method: action.payload.method,
            message: action.payload.message
          };
          state.isAuthenticated = false;
        } else {
          state.twoFactorPending = false;
          state.twoFactorData = null;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          try {
            if (action.payload.user) {
              localStorage.setItem('user', JSON.stringify(action.payload.user));
            }
          } catch (_) {}
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })
      // 2FA Verification
      .addCase(verify2FALogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verify2FALogin.fulfilled, (state, action) => {
        state.loading = false;
        state.twoFactorPending = false;
        state.twoFactorData = null;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        try {
          if (action.payload.user) {
            localStorage.setItem('user', JSON.stringify(action.payload.user));
          }
        } catch (_) {}
      })
      .addCase(verify2FALogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Signup
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get profile
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        const profileUser = action.payload?.user || action.payload;
        state.user = profileUser;
        state.isAuthenticated = true;
        try {
          if (profileUser) {
            localStorage.setItem('user', JSON.stringify(profileUser));
          }
        } catch (_) {}
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        // Do NOT drop authentication on profile load failure.
        // Network glitches, cold-starts, or timeouts must never log the user out on reload.
      })
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload.user || action.payload;
        state.user = updated;
        try {
          if (updated) {
            localStorage.setItem('user', JSON.stringify(updated));
          }
        } catch (_) {}
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError, cancel2FAPending, supersededByRemoteLogin } = authSlice.actions;
export default authSlice.reducer;
